/**
 * Date utility functions for DatePicker component
 */

/**
 * Format date for display in the input
 * @param date - The date to format
 * @param showTime - Whether to include time in the format
 */
export const formatDate = (date: Date | undefined, showTime: boolean = false): string => {
	if (!date || !isValidDate(date) || isNaN(date.getTime())) {
		return "";
	}

	const dateStr = date.toLocaleDateString("en-US", {
		day: "numeric",
		month: "long",
		year: "numeric",
	});

	if (showTime) {
		const timeStr = date.toLocaleTimeString("en-US", {
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		});
		return `${dateStr} ${timeStr}`;
	}

	return dateStr;
};

/**
 * Check if a date is valid
 */
export const isValidDate = (date: Date | undefined): boolean => {
	if (!date) {
		return false;
	}
	return !isNaN(date.getTime());
};

/**
 * Parse date from various string formats
 */
export const parseDate = (value: string): Date | undefined => {
	if (!value.trim()) {
		return undefined;
	}

	// Try parsing the value as a date
	const date = new Date(value);
	return isValidDate(date) ? date : undefined;
};

/**
 * Validate date against min/max constraints
 */
export const isDateInRange = (date: Date, startDate?: Date, endDate?: Date): boolean => {
	if (startDate && date.getTime() < startDate.getTime()) {
		return false;
	}
	if (endDate && date.getTime() > endDate.getTime()) {
		return false;
	}
	return true;
};

/**
 * Process and validate user input, returning the processed result
 */
export const processDateInput = (
	inputValue: string,
	currentValue: Date | null | undefined,
	startDate?: Date,
	endDate?: Date,
	allowEmpty?: boolean
): {
	isValid: boolean;
	date: Date | null;
	shouldReset: boolean;
} => {
	// Handle empty input
	if (inputValue === "") {
		return {
			isValid: true,
			date: allowEmpty ? null : currentValue || null,
			shouldReset: !allowEmpty,
		};
	}

	// Parse the date
	const parsedDate = parseDate(inputValue);
	if (!parsedDate || !isValidDate(parsedDate)) {
		return {
			isValid: false,
			date: currentValue || null,
			shouldReset: true,
		};
	}

	// Validate range
	if (!isDateInRange(parsedDate, startDate, endDate)) {
		return {
			isValid: false,
			date: currentValue || null,
			shouldReset: true,
		};
	}

	return {
		isValid: true,
		date: parsedDate,
		shouldReset: false,
	};
};
